import { useSimStore } from '../../store/useSimStore';
import { computeVentNumerics, AGENTS, coupledEtco2, coupledSpo2, fio2Responsiveness } from '../../engine/ventEngine';
import styles from './VentilatorControls.module.css';

// Ramp (ms) over which a ventilator change is reflected in EtCO₂ / SpO₂.
// Short enough to feel responsive when teaching, slow enough to look physiological.
const VENT_COUPLE_MS = 10000;

const MODES = ['VCV', 'PCV', 'PSV'];
const IE_OPTIONS = ['2:1', '1:1', '1:1.5', '1:2', '1:2.5', '1:3'];
const AGENT_OPTIONS = [
  { key: 'none', label: 'None' },
  { key: 'sevo', label: 'Sevo' },
  { key: 'des',  label: 'Des' },
  { key: 'iso',  label: 'Iso' },
];

/**
 * Instructor ventilator-settings panel. Drives the ventilator screen on the
 * learner monitor. Shown when split-screen is enabled.
 *
 * VTe, Ppeak and Pplat are directly editable; Pplat is constrained between
 * PEEP and Ppeak so the displayed pressures stay physiologically ordered.
 */
export default function VentilatorControls() {
  const ventSettings = useSimStore((s) => s.ventSettings);
  const setVentSetting = useSimStore((s) => s.setVentSetting);
  // EtCO₂ is the shared monitor vital (drives capnography) — edit it directly
  const etco2Target = useSimStore((s) => s.vitals.EtCO2?.target ?? 0);
  const setVitalTarget = useSimStore((s) => s.setVitalTarget);

  const { mode, vte, ppeak, pplat, peep, agent } = ventSettings;
  const derived = computeVentNumerics(ventSettings);

  // Keep PEEP ≤ Pplat ≤ Ppeak as values are dragged
  const setPpeak = (v) => {
    setVentSetting('ppeak', v);
    if (pplat > v) setVentSetting('pplat', v);
  };
  const setPplat = (v) => {
    const clamped = Math.min(Math.max(v, peep), ppeak);
    setVentSetting('pplat', clamped);
  };
  const setPeep = (v) => {
    setVentSetting('peep', v);
    if (pplat < v) setVentSetting('pplat', v);
  };

  // ── Physiological coupling: MV → EtCO₂, FiO₂ → SpO₂ ─────────────────────────
  // Read fresh state via getState() so rapid slider drags compose correctly.
  const setVte = (v) => {
    const s = useSimStore.getState();
    const mvOld = (s.ventSettings.vte * s.ventSettings.rr) / 1000;
    const mvNew = (v * s.ventSettings.rr) / 1000;
    s.setVentSetting('vte', v);
    const et = s.vitals.EtCO2.target ?? s.vitals.EtCO2.current;
    s.applyVitals({ EtCO2: coupledEtco2(et, mvOld, mvNew) }, VENT_COUPLE_MS);
  };
  const setRate = (v) => {
    const s = useSimStore.getState();
    const mvOld = (s.ventSettings.vte * s.ventSettings.rr) / 1000;
    const mvNew = (s.ventSettings.vte * v) / 1000;
    s.setVentSetting('rr', v);
    const et = s.vitals.EtCO2.target ?? s.vitals.EtCO2.current;
    s.applyVitals({ EtCO2: coupledEtco2(et, mvOld, mvNew) }, VENT_COUPLE_MS);
  };
  const setFio2 = (v) => {
    const s = useSimStore.getState();
    const oldF = s.ventSettings.fio2;
    s.setVentSetting('fio2', v);
    const sp = s.vitals.SpO2.target ?? s.vitals.SpO2.current;
    const resp = fio2Responsiveness(s.activeScenario, s.activeModifier);
    s.applyVitals({ SpO2: coupledSpo2(sp, oldF, v, resp) }, VENT_COUPLE_MS);
  };

  return (
    <div className={styles.panel}>
      {/* Mode selector */}
      <div className={styles.modeRow}>
        {MODES.map((m) => (
          <button
            key={m}
            className={`${styles.modeBtn} ${mode === m ? styles.modeBtnActive : ''}`}
            onClick={() => setVentSetting('mode', m)}
          >
            {m}
          </button>
        ))}
      </div>

      <div className={styles.sliderGrid}>
        <Slider label="VTe" unit="mL" min={100} max={1000} step={10}
          value={vte} onChange={setVte} />
        <Slider label="Ppeak" unit="cmH₂O" min={5} max={60} step={1}
          value={ppeak} onChange={setPpeak} />
        <Slider label="Pplat" unit="cmH₂O" min={peep} max={ppeak} step={1}
          value={pplat} onChange={setPplat} />
        <Slider label="Rate" unit="/min" min={4} max={40} step={1}
          value={ventSettings.rr} onChange={setRate} />
        <Slider label="PEEP" unit="cmH₂O" min={0} max={20} step={1}
          value={peep} onChange={setPeep} />
        <Slider label="FiO₂" unit="%" min={21} max={100} step={1}
          value={ventSettings.fio2} onChange={setFio2} />
        <Slider label="Psupport" unit="cmH₂O" min={0} max={30} step={1}
          value={ventSettings.psupport} onChange={(v) => setVentSetting('psupport', v)} />
        <Slider label="Flow Trigger" unit="L/min" min={0.2} max={5} step={0.1}
          value={ventSettings.flowTrigger} onChange={(v) => setVentSetting('flowTrigger', v)} />
        <Slider label="Fresh Gas Flow" unit="L/min" min={0.3} max={10} step={0.1}
          value={ventSettings.totalFlow} onChange={(v) => setVentSetting('totalFlow', v)} />

        {/* I:E selector */}
        <div className={styles.field}>
          <div className={styles.fieldHead}>
            <span className={styles.fieldLabel}>I:E Ratio</span>
            <span className={styles.fieldValue}>{ventSettings.ie}</span>
          </div>
          <div className={styles.ieRow}>
            {IE_OPTIONS.map((ie) => (
              <button
                key={ie}
                className={`${styles.ieBtn} ${ventSettings.ie === ie ? styles.ieBtnActive : ''}`}
                onClick={() => setVentSetting('ie', ie)}
              >
                {ie}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── Gas analyser ──────────────────────────────────────────────── */}
      <div className={styles.gasHeader}>Gas Analyser</div>
      <div className={styles.sliderGrid}>
        <Slider label="EtCO₂" unit="mmHg" min={0} max={80} step={1}
          value={Math.round(etco2Target)} onChange={(v) => setVitalTarget('EtCO2', v)} />
        <Slider label="FiCO₂" unit="mmHg" min={0} max={15} step={1}
          value={ventSettings.fico2} onChange={(v) => setVentSetting('fico2', v)} />

        {/* Anaesthetic agent selector */}
        <div className={styles.field}>
          <div className={styles.fieldHead}>
            <span className={styles.fieldLabel}>Agent</span>
            <span className={styles.fieldValue}>{AGENTS[agent]?.label ?? '—'}</span>
          </div>
          <div className={styles.ieRow}>
            {AGENT_OPTIONS.map((a) => (
              <button
                key={a.key}
                className={`${styles.ieBtn} ${agent === a.key ? styles.ieBtnActive : ''}`}
                onClick={() => setVentSetting('agent', a.key)}
              >
                {a.label}
              </button>
            ))}
          </div>
        </div>

        {agent !== 'none' && (
          <Slider label="ET Agent" unit="%" min={0} max={12} step={0.1}
            value={ventSettings.agentPct} onChange={(v) => setVentSetting('agentPct', v)} />
        )}

        {/* Nitrous oxide — delivered %, adds to total MAC (N₂O MAC ≈ 104%) */}
        <Slider label="N₂O" unit="%" min={0} max={75} step={1}
          value={ventSettings.n2o ?? 0} onChange={(v) => setVentSetting('n2o', v)} />
      </div>

      {/* Live derived readback */}
      <div className={styles.readback}>
        <Readout label="MV" value={`${derived.mv} L/min`} />
        <Readout label="Driving P" value={`${Math.max(0, pplat - peep)} cmH₂O`} />
        <Readout label="Compliance" value={`${derived.compliance} mL/cmH₂O`} />
        <Readout label="Resistance" value={`${derived.resistance} cmH₂O·s/L`} />
        {agent !== 'none' && <Readout label="MAC" value={`${derived.mac.toFixed(1)}`} />}
      </div>
    </div>
  );
}

function Slider({ label, unit, min, max, step, value, onChange }) {
  return (
    <div className={styles.field}>
      <div className={styles.fieldHead}>
        <span className={styles.fieldLabel}>{label}</span>
        <span className={styles.fieldValue}>{value} <small>{unit}</small></span>
      </div>
      <input
        type="range"
        className={styles.slider}
        min={min} max={max} step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
      />
    </div>
  );
}

function Readout({ label, value }) {
  return (
    <div className={styles.readout}>
      <span className={styles.readoutLabel}>{label}</span>
      <span className={styles.readoutValue}>{value}</span>
    </div>
  );
}
